import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  consumeCreditUnlessAdmin,
  refundCreditUnlessBypassed,
  InsufficientCreditsError,
} from "./_credits";

const STYLE_NAMES = {
  oscola: "OSCOLA (Oxford footnote style)",
  harvard: "Harvard author-date",
  "apa-7": "APA 7th edition",
  "sa-citation": "South African legal citation (cases, statutes, GN/GG)",
} as const;

type StyleSlug = keyof typeof STYLE_NAMES;

const DISCLAIMER =
  "AI-generated. Verify every citation against the primary source (gov.za, official law report, or publisher record) before relying on it.";

async function callGateway(
  messages: { role: string; content: string }[],
  temperature = 0.2,
  jsonMode = false,
) {
  const key = process.env.LOVABLE_API_KEY;
  if (!key) throw new Error("AI not configured");
  const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Lovable-API-Key": key,
    },
    body: JSON.stringify({
      model: "google/gemini-3-flash-preview",
      messages,
      temperature,
      ...(jsonMode ? { response_format: { type: "json_object" } } : {}),
    }),
  });
  if (!res.ok) {
    const status = res.status;
    if (status === 429) throw new Error("RATE_LIMIT");
    if (status === 402) throw new Error("CREDITS_EXHAUSTED");
    throw new Error("AI_UNAVAILABLE");
  }
  const data = (await res.json()) as {
    choices?: { message?: { content?: string } }[];
  };
  return data.choices?.[0]?.message?.content?.trim() ?? "";
}

// ---------------- Single citation ----------------

const citationInput = z.object({
  style: z.enum(["oscola", "harvard", "apa-7", "sa-citation"]),
  sourceType: z.enum([
    "case",
    "statute",
    "journal_article",
    "book",
    "chapter",
    "government_notice",
    "website",
    "other",
  ]),
  details: z.string().trim().min(8, "Tell us at least a sentence about the source").max(2000),
  pinpoint: z.string().trim().max(120).optional().default(""),
});

export const generateCitation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => citationInput.parse(i))
  .handler(async ({ data, context }) => {
    const ref = `citation:${crypto.randomUUID()}`;
    let balance: number;
    let bypassed = false;
    try {
      const r = await consumeCreditUnlessAdmin(context.userId, "ai_citation", ref);
      balance = r.balance;
      bypassed = r.bypassed;
    } catch (e) {
      if (e instanceof InsufficientCreditsError) throw new Error("INSUFFICIENT_CREDITS");
      throw e;
    }

    try {
      const styleName = STYLE_NAMES[data.style as StyleSlug];
      const system =
        `You are a meticulous legal-citation generator for South African LLB students. ` +
        `Produce ONE citation in ${styleName}. ` +
        `Rules: \n` +
        `1. Output ONLY the citation itself. No preface, no explanation, no markdown formatting beyond the citation text.\n` +
        `2. If you are not certain of a fact (year, volume, page, edition, court abbreviation), use [REQUIRES VERIFICATION] in square brackets in that exact place instead of guessing.\n` +
        `3. For statutes, use the official short title and 'Act X of YYYY' for South African legislation.\n` +
        `4. For cases, italicise party names using *asterisks* on either side of the party block (e.g. *Carmichele v Minister of Safety and Security*).\n` +
        `5. Never invent citations. If the source seems fabricated or impossible, output: 'INSUFFICIENT INFORMATION — please provide a verifiable source.'`;

      const user =
        `Style: ${styleName}\n` +
        `Source type: ${data.sourceType}\n` +
        `Pinpoint reference: ${data.pinpoint || "(none)"}\n` +
        `Source details:\n${data.details}`;

      const citation = await callGateway([
        { role: "system", content: system },
        { role: "user", content: user },
      ]);

      if (!citation || citation.toLowerCase().includes("insufficient information")) {
        await refundCreditUnlessBypassed(context.userId, "ai_citation_refund", ref, bypassed);
        return {
          ok: false as const,
          error: "Not enough detail to produce a verified citation. Please add more source information.",
          balance: bypassed ? balance : balance + 1,
          bypassed,
        };
      }

      return {
        ok: true as const,
        citation,
        disclaimer: DISCLAIMER,
        balance,
        bypassed,
      };
    } catch (err) {
      await refundCreditUnlessBypassed(context.userId, "ai_citation_refund", ref, bypassed);
      const message = err instanceof Error ? err.message : "AI_UNAVAILABLE";
      throw new Error(message);
    }
  });

// ---------------- Footnotes ----------------

const footnoteInput = z.object({
  style: z.enum(["oscola", "harvard", "apa-7", "sa-citation"]),
  passage: z
    .string()
    .trim()
    .min(20, "Paste at least a sentence of your draft.")
    .max(6000, "Footnote generator accepts up to ~6,000 characters at a time."),
});

export const generateFootnotes = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => footnoteInput.parse(i))
  .handler(async ({ data, context }) => {
    const ref = `footnotes:${crypto.randomUUID()}`;
    let balance: number;
    let bypassed = false;
    try {
      const r = await consumeCreditUnlessAdmin(context.userId, "ai_footnotes", ref);
      balance = r.balance;
      bypassed = r.bypassed;
    } catch (e) {
      if (e instanceof InsufficientCreditsError) throw new Error("INSUFFICIENT_CREDITS");
      throw e;
    }

    try {
      const styleName = STYLE_NAMES[data.style as StyleSlug];
      const system =
        `You are a legal-writing assistant that inserts numbered footnote markers into a student's draft and produces a matching footnote list. ` +
        `Use ${styleName}. ` +
        `Rules:\n` +
        `1. Identify legal propositions, statute references and case mentions in the passage that need authority.\n` +
        `2. Insert superscript-style numbered markers as ^1, ^2, ^3 at the END of the sentence containing the proposition (do NOT rewrite the passage otherwise).\n` +
        `3. After the passage, output a heading 'Footnotes:' and a numbered list with each footnote in ${styleName} form.\n` +
        `4. Where you cannot verify the exact citation, mark the footnote with [REQUIRES VERIFICATION]. Never invent case names, volumes, pages, or section numbers.\n` +
        `5. Output format EXACTLY:\n\n--- PASSAGE WITH MARKERS ---\n<passage>\n\n--- FOOTNOTES ---\n1. ...\n2. ...`;

      const user = `Insert footnotes for this draft:\n\n${data.passage}`;
      const out = await callGateway([
        { role: "system", content: system },
        { role: "user", content: user },
      ]);

      if (!out) {
        await refundCreditUnlessBypassed(context.userId, "ai_footnotes_refund", ref, bypassed);
        return {
          ok: false as const,
          error: "Could not generate footnotes. Try a shorter, clearer passage.",
          balance: bypassed ? balance : balance + 1,
          bypassed,
        };
      }

      return { ok: true as const, output: out, disclaimer: DISCLAIMER, balance, bypassed };
    } catch (err) {
      await refundCreditUnlessBypassed(context.userId, "ai_footnotes_refund", ref, bypassed);
      const message = err instanceof Error ? err.message : "AI_UNAVAILABLE";
      throw new Error(message);
    }
  });

// ---------------- Bibliography ----------------

const bibliographyInput = z.object({
  style: z.enum(["oscola", "harvard", "apa-7", "sa-citation"]),
  sources: z
    .string()
    .trim()
    .min(20, "Add at least one source.")
    .max(10000, "Bibliography generator accepts up to ~10,000 characters of source notes."),
  mode: z.enum(["bibliography", "reference_list", "footnote_list"]).default("bibliography"),
});

export const generateBibliography = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => bibliographyInput.parse(i))
  .handler(async ({ data, context }) => {
    const ref = `bibliography:${crypto.randomUUID()}`;
    let balance: number;
    let bypassed = false;
    try {
      const r = await consumeCreditUnlessAdmin(context.userId, "ai_bibliography", ref);
      balance = r.balance;
      bypassed = r.bypassed;
    } catch (e) {
      if (e instanceof InsufficientCreditsError) throw new Error("INSUFFICIENT_CREDITS");
      throw e;
    }

    try {
      const styleName = STYLE_NAMES[data.style as StyleSlug];
      const modeLabel =
        data.mode === "reference_list"
          ? "Reference list (in-text style, alphabetised by author surname)"
          : data.mode === "footnote_list"
            ? "Footnote list (numbered 1, 2, 3 … in the order the user listed the sources)"
            : "Bibliography (alphabetised by author surname; categorised: Cases, Legislation, Books, Journal articles, Internet sources, Government publications)";
      const system =
        `You are a legal bibliography generator for South African students. Convert the user's raw source notes into a clean ${modeLabel} in ${styleName}.\n` +
        `Rules:\n` +
        `1. Output ONLY the formatted list. No commentary, no markdown fences.\n` +
        `2. Split user input by newlines or semicolons; each non-empty line is one source.\n` +
        `3. Where a year, volume, page, court abbreviation or edition is uncertain, use [REQUIRES VERIFICATION] in that exact spot. Never invent.\n` +
        `4. Italicise case names with *asterisks* (e.g. *S v Makwanyane*).\n` +
        `5. For bibliography mode, group with headings (Cases / Legislation / Books / Journal articles / Internet sources / Government publications). Skip empty groups.\n` +
        `6. If you cannot tell what a line is, list it under "Other" and flag with [REQUIRES VERIFICATION].`;
      const user = `Style: ${styleName}\nMode: ${data.mode}\n\nSources:\n${data.sources}`;
      const out = await callGateway([
        { role: "system", content: system },
        { role: "user", content: user },
      ]);

      if (!out) {
        await refundCreditUnlessBypassed(context.userId, "ai_bibliography_refund", ref, bypassed);
        return {
          ok: false as const,
          error: "Could not produce a bibliography. Add more detail to each source.",
          balance: bypassed ? balance : balance + 1,
          bypassed,
        };
      }
      return { ok: true as const, output: out, disclaimer: DISCLAIMER, balance, bypassed };
    } catch (err) {
      await refundCreditUnlessBypassed(context.userId, "ai_bibliography_refund", ref, bypassed);
      const message = err instanceof Error ? err.message : "AI_UNAVAILABLE";
      throw new Error(message);
    }
  });

// ---------------- Bibliography source verifier ----------------
//
// Runs BEFORE the user spends a credit on a full bibliography. Reads each
// source line, flags missing publication details / non-credible sources, and
// proposes a corrected version. Does NOT claim a source exists — only checks
// shape + credibility heuristics.

const verifyInput = z.object({
  sources: z
    .string()
    .trim()
    .min(10, "Paste at least one source.")
    .max(10000),
  style: z.enum(["oscola", "harvard", "apa-7", "sa-citation"]).default("sa-citation"),
});

export const verifyBibliographySources = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => verifyInput.parse(i))
  .handler(async ({ data, context }) => {
    const ref = `verify-bib:${crypto.randomUUID()}`;
    let balance: number;
    let bypassed = false;
    try {
      const r = await consumeCreditUnlessAdmin(context.userId, "ai_verify_bib", ref);
      balance = r.balance;
      bypassed = r.bypassed;
    } catch (e) {
      if (e instanceof InsufficientCreditsError) throw new Error("INSUFFICIENT_CREDITS");
      throw e;
    }

    try {
      const styleName = STYLE_NAMES[data.style];
      const system =
        `You are a strict legal-source verifier for ${styleName}. ` +
        `For each line in the user's input, return a JSON array (no prose) of items with shape: ` +
        `{ "input": string, "credibility": "credible"|"questionable"|"non_credible"|"unknown", ` +
        `"missing": string[], "suggested_correction": string|null, "notes": string }.\n` +
        `Rules:\n` +
        `1. "credible" = primary source / reputable publisher / official gazette. "questionable" = blog, unverified site, unclear publisher. "non_credible" = Wikipedia, random forums, Q&A sites, study-cheat sites.\n` +
        `2. "missing" lists the publication details that are missing for a complete ${styleName} entry (e.g. ["year","court","volume","section"]).\n` +
        `3. "suggested_correction" is a rewritten source line that the user should aim for, with [REQUIRES VERIFICATION] in any spot you can't determine. Never fabricate.\n` +
        `4. Output ONLY the JSON array. Split input on newlines or semicolons; one item per non-empty line.`;
      const out = await callGateway(
        [
          { role: "system", content: system },
          { role: "user", content: data.sources },
        ],
        0.1,
        true,
      );
      let parsed: any[] = [];
      try {
        const cleaned = out.replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "");
        const j = JSON.parse(cleaned);
        parsed = Array.isArray(j) ? j : Array.isArray(j?.items) ? j.items : [];
      } catch {
        parsed = [];
      }
      if (parsed.length === 0) {
        await refundCreditUnlessBypassed(context.userId, "ai_verify_bib_refund", ref, bypassed);
        return {
          ok: false as const,
          error: "Could not verify your sources. Try again with one source per line.",
          balance: bypassed ? balance : balance + 1,
          bypassed,
        };
      }
      return { ok: true as const, items: parsed, balance, bypassed, disclaimer: DISCLAIMER };
    } catch (err) {
      await refundCreditUnlessBypassed(context.userId, "ai_verify_bib_refund", ref, bypassed);
      const message = err instanceof Error ? err.message : "AI_UNAVAILABLE";
      throw new Error(message);
    }
  });

// ---------------- My Citations: save / list / update / delete ----------------

const saveInput = z.object({
  title: z.string().trim().min(1).max(200).default("Untitled"),
  kind: z.enum([
    "bibliography",
    "reference_list",
    "footnote_list",
    "single_citation",
    "footnotes_passage",
  ]),
  style: z.string().trim().min(1).max(40),
  sourceType: z.string().trim().max(40).optional().nullable(),
  inputText: z.string().trim().min(1).max(20000),
  outputText: z.string().trim().min(1).max(40000),
  verification: z.array(z.any()).optional().default([]),
});

export const saveCitation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => saveInput.parse(i))
  .handler(async ({ data, context }) => {
    const { data: row, error } = await context.supabase
      .from("user_citations")
      .insert({
        user_id: context.userId,
        title: data.title,
        kind: data.kind,
        style: data.style,
        source_type: data.sourceType ?? null,
        input_text: data.inputText,
        output_text: data.outputText,
        verification: data.verification ?? [],
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id };
  });

export const listMyCitations = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("user_citations")
      .select("id, title, kind, style, source_type, output_text, pinned, created_at, updated_at")
      .eq("user_id", context.userId)
      .order("pinned", { ascending: false })
      .order("updated_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return { citations: data ?? [] };
  });

export const getMyCitation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    const { data: row, error } = await context.supabase
      .from("user_citations")
      .select("*")
      .eq("id", data.id)
      .eq("user_id", context.userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) throw new Error("Not found");
    return { citation: row };
  });

const updateInput = z.object({
  id: z.string().uuid(),
  title: z.string().trim().min(1).max(200).optional(),
  outputText: z.string().trim().min(1).max(40000).optional(),
  pinned: z.boolean().optional(),
});

export const updateMyCitation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => updateInput.parse(i))
  .handler(async ({ data, context }) => {
    const patch: { title?: string; output_text?: string; pinned?: boolean } = {};
    if (data.title !== undefined) patch.title = data.title;
    if (data.outputText !== undefined) patch.output_text = data.outputText;
    if (data.pinned !== undefined) patch.pinned = data.pinned;
    if (!Object.keys(patch).length) return { ok: true };
    const { error } = await context.supabase
      .from("user_citations")
      .update(patch)
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteMyCitation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("user_citations")
      .delete()
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
